"use client";

import { useCallback, useRef, useState } from "react";
import Link from "next/link";

import { PricingLink } from "@/components/platform/PricingLink";
import { Button } from "@/components/ui/stateful-button";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { usePlatformAuth } from "@/hooks/use-platform-auth";
import { cn } from "@/lib/utils";

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block space-y-2", className)}>
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}

export function ContactPageContent() {
  const { isAuthenticated } = usePlatformAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [focused, setFocused] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const inputClass = (field: string) =>
    cn(
      "w-full border-0 border-b bg-transparent pb-3 pt-1 text-[15px] text-neutral-900 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-neutral-400",
      focused === field
        ? "border-neutral-900 shadow-[0_1px_0_0_rgba(0,0,0,0.9)]"
        : "border-neutral-200",
    );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim() || !email.trim() || !message.trim()) {
        toast.error("Please fill in all fields");
        return;
      }
      setSending(true);
      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim(),
            message: message.trim(),
          }),
        });
        if (!res.ok) throw new Error("send_failed");
        toast.success("Message sent. We will get back to you soon.");
        setName("");
        setEmail("");
        setMessage("");
      } catch {
        toast.error("Could not send your message. Please try again.");
      } finally {
        setSending(false);
      }
    },
    [name, email, message],
  );

  return (
    <div className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-indigo-500/10 blur-[100px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-amber-500/8 blur-[120px]"
        />

        <div
          className={cn(
            "relative mx-auto grid max-w-6xl gap-16 px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20 lg:px-8",
            !isAuthenticated && "pt-10 sm:pt-14 lg:pt-20",
          )}
        >
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={cn(
              "flex flex-col justify-center lg:min-h-[520px]",
              isAuthenticated ? "pt-8 lg:pt-12" : "pt-0",
            )}
          >
            <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500">
              Contact
            </p>
            <h1 className="mb-6 max-w-md text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.08] tracking-[-0.03em] text-white">
              Thoughtful tools deserve thoughtful conversation.
            </h1>
            <p className="max-w-sm text-[15px] leading-[1.8] text-neutral-400">
              Questions about Inora, partnerships, or feedback on the product.
              Write when it matters. We read everything that reaches us.
            </p>
            <div className="mt-10 flex items-center gap-5 text-[13px] text-neutral-500">
              <PricingLink className="transition-colors hover:text-neutral-200">
                Pricing
              </PricingLink>
            </div>
          </motion.aside>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.08 }}
            className={cn(
              "relative",
              isAuthenticated && "mt-10 lg:mt-24",
            )}
          >
            <div className="overflow-hidden rounded-[20px] border border-white/[0.08] bg-[#f7f5f2] shadow-[0_40px_100px_-40px_rgba(0,0,0,0.65)]">
              <form
                onSubmit={onSubmit}
                className="flex min-h-[min(72vh,640px)] flex-col px-8 py-10 sm:px-12 sm:py-12"
              >
                <p className="font-serif text-[clamp(1.35rem,2.5vw,1.75rem)] leading-snug text-neutral-800">
                  Dear Inora team,
                </p>

                <div className="relative mt-8 flex-1">
                  <textarea
                    ref={messageRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onFocus={() => setFocused("message")}
                    onBlur={() => setFocused(null)}
                    placeholder="Your message"
                    rows={8}
                    className={cn(
                      "h-full min-h-[200px] w-full resize-none bg-transparent text-[17px] leading-[1.75] text-neutral-800 outline-none placeholder:text-neutral-400",
                      "caret-neutral-900",
                    )}
                  />
                </div>

                <div className="mt-8 grid gap-8 border-t border-neutral-200/80 pt-8 sm:grid-cols-2">
                  <Field label="Your name">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onFocus={() => setFocused("name")}
                      onBlur={() => setFocused(null)}
                      autoComplete="name"
                      className={inputClass("name")}
                      placeholder="Name"
                    />
                  </Field>
                  <Field label="Your email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocused("email")}
                      onBlur={() => setFocused(null)}
                      autoComplete="email"
                      className={inputClass("email")}
                      placeholder="you@email.com"
                    />
                  </Field>
                </div>

                <div className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="max-w-xs text-[11px] leading-relaxed text-neutral-500">
                    By sending, you agree we may use your message to respond.
                    See our{" "}
                    <Link
                      href="/privacy"
                      className="underline underline-offset-2 hover:text-neutral-700"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                  <Button
                    onClick={async (e: any) => {
                      await onSubmit(e);
                    }}
                    type="submit"
                    disabled={sending}
                    className="shrink-0 rounded-full bg-[#5c4d9e] px-8 py-3.5 text-[14px] font-medium text-white transition-colors hover:bg-[#6a5aad] !border-0 !outline-none !ring-0 focus:!outline-none focus:!ring-0 active:scale-[0.98] disabled:opacity-60"
                  >
                    Send message
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
  );
}
